/**
 * StaffLifecycleModal — NOVA-031 confirm dialog for the owner-gated staff-lifecycle trio (suspend /
 * revoke / archive). Each action is destructive to the member's access, so it requires an explicit
 * confirmation (no overlay/Esc dismissal on a pending write). Delegates to the lab-admin
 * POST /lab-admin/staff/:id/statut endpoint; the server flips both the membership statut (cap-cache drop)
 * and the identity statut (session-kill + login reject). The surface is owner/manager-gated upstream by
 * iam.utilisateur.manage (the modal only opens from the staff table, itself gated).
 */
import { Button, Modal, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { useSetStaffStatut, type StaffLifecycleAction } from "../api";
import type { StaffMember } from "../types";

export interface StaffLifecycleModalProps {
  member: StaffMember | null;
  action: StaffLifecycleAction | null;
  labId: string | null;
  onClose: () => void;
}

/** danger for the terminal/withdrawing actions, primary (caution) for the reversible suspend. */
const VARIANT: Record<StaffLifecycleAction, "primary" | "danger"> = {
  suspend: "primary",
  revoke: "danger",
  archive: "danger",
};

export function StaffLifecycleModal({ member, action, labId, onClose }: StaffLifecycleModalProps) {
  const { t } = useT();
  const toast = useToast();
  const setStatut = useSetStaffStatut(labId);

  const open = !!member && !!action;

  const confirm = () => {
    if (!member || !action) return;
    setStatut.mutate(
      { utilisateur_id: member.utilisateur.id, action },
      {
        onSuccess: () => {
          toast.success(t(`admin.team.lifecycle.${action}.done`));
          onClose();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("admin.toast.error")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!setStatut.isPending) onClose();
      }}
      dismissable={!setStatut.isPending}
      title={action ? t(`admin.team.lifecycle.${action}.title`) : ""}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={setStatut.isPending}>
            {t("admin.action.cancel")}
          </Button>
          <Button
            variant={action ? VARIANT[action] : "primary"}
            onClick={confirm}
            loading={setStatut.isPending}
          >
            {action ? t(`admin.team.lifecycle.${action}.confirm`) : t("admin.action.confirm")}
          </Button>
        </>
      }
    >
      {member && action && (
        <p style={{ margin: 0 }}>
          {t(`admin.team.lifecycle.${action}.body`, { name: member.utilisateur.nomComplet || member.utilisateur.email })}
        </p>
      )}
    </Modal>
  );
}
